import { Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { isUndefined } from 'lodash';
import { useEffect } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
  DispositiveProvisionTransferTaxKind,
} from '@/types/schema';

import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormPaths,
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsTemplateSlotKind,
} from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { DispositiveProvisionsTemplateEditorTabHeader } from '../DispositiveProvisionsTemplateSplitScreenModal.components';
import { BLANK_DISCLAIMER_TRUST } from '../DispositiveProvisionsTemplateSplitScreenModal.constants';
import {
  DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE,
  DispositiveProvisionsTemplateShape,
  MaritalTrustType,
} from '../DispositiveProvisionsTemplateSplitScreenModal.types';
import {
  BLANK_BYPASS_TRUST,
  TemplateRecipientSlot,
} from './DispositiveProvisionsTemplateSplitScreenModal.components';

export function DispositiveProvisionsTemplateMaritalTrustPreset() {
  const { control, setValue } =
    useFormContext<DispositiveProvisionsTemplateShape>();
  const {
    append,
    replace,
    fields: recipientFields,
  } = useFieldArray({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const satisfies DispositiveProvisionsFormPaths,
    keyName: '_id',
  });
  const [maritalTrustType, recipients] = useWatch({
    control,
    name: [
      `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.maritalTrustType`,
      `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients`,
    ],
  });

  const maritalTrustTypePrevious = usePrevious(maritalTrustType);

  useEffect(() => {
    if (isUndefined(maritalTrustTypePrevious)) return;
    if (maritalTrustType === maritalTrustTypePrevious) return;

    const additionalSlotKinds = [
      DispositiveProvisionsTemplateSlotKind.MaritalDisclaimer,
      DispositiveProvisionsTemplateSlotKind.MaritalBypass,
      DispositiveProvisionsTemplateSlotKind.MaritalClaytonQtip,
    ];
    const baseRecipients = recipients.filter(
      (recipient) =>
        !additionalSlotKinds.some(
          (slotKind) => slotKind === recipient._templateSlotKind
        )
    );
    const maritalTrustIndex = baseRecipients.findIndex(
      (recipient) =>
        recipient._templateSlotKind ===
        DispositiveProvisionsTemplateSlotKind.MaritalTrust
    );
    const updatedBaseRecipients: DispositiveProvisionsFormRecipient[] =
      baseRecipients.map(
        (recipient, index): DispositiveProvisionsFormRecipient => {
          if (index !== maritalTrustIndex) return recipient;
          if (maritalTrustType === MaritalTrustType.None) {
            return {
              ...recipient,
              dispositionKind_ButtonGroup:
                DispositiveProvisionDispositionKind.Percentage,
              dispositionKind_Select: '',
              dispositionPercentage: new Decimal(100),
            };
          }
          return {
            ...recipient,
            dispositionKind_ButtonGroup: 'other',
            dispositionKind_Select:
              DispositiveProvisionDispositionKind.AnythingLeftOver,
            dispositionPercentage: null,
          };
        }
      );
    let updatedRecipients = updatedBaseRecipients;
    if (maritalTrustType === MaritalTrustType.DisclaimerTrust) {
      updatedRecipients = [
        {
          ...BLANK_DISCLAIMER_TRUST,
          _templateSlotKind:
            DispositiveProvisionsTemplateSlotKind.MaritalDisclaimer,
        },
        ...updatedBaseRecipients,
      ];
    } else if (maritalTrustType === MaritalTrustType.ClaytonQTIPTrust) {
      updatedRecipients = [
        {
          ...BLANK_BYPASS_TRUST,
          _templateSlotKind:
            DispositiveProvisionsTemplateSlotKind.MaritalBypass,
        },
        {
          ...BLANK_CLAYTON_QTIP_TRUST,
          _templateSlotKind:
            DispositiveProvisionsTemplateSlotKind.MaritalClaytonQtip,
        },
        ...updatedBaseRecipients,
      ];
    }
    replace(updatedRecipients);
  }, [maritalTrustType, maritalTrustTypePrevious, recipients, replace]);

  const handleRemoveRecipient = (index: number) => {
    const recipient = recipients[index];
    replace(recipients.filter((_, recipientIndex) => recipientIndex !== index));
    if (
      [
        DispositiveProvisionsTemplateSlotKind.MaritalDisclaimer,
        DispositiveProvisionsTemplateSlotKind.MaritalBypass,
        DispositiveProvisionsTemplateSlotKind.MaritalClaytonQtip,
      ].some((slotKind) => slotKind === recipient?._templateSlotKind)
    ) {
      setValue(
        `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.maritalTrustType`,
        MaritalTrustType.None
      );
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      <DispositiveProvisionsTemplateEditorTabHeader
        templateType={DispositiveProvisionTemplateKind.MaritalTrust}
        recipients={recipientFields}
        onAddTemplateRecipient={append}
      />
      <Card variant="filled-callout" sx={{ padding: 3 }}>
        <FormAwareRadioGroup<DispositiveProvisionsTemplateShape>
          control={control}
          label="Include disclaimer trust"
          hideLabel
          fieldName={`${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.maritalTrustType`}
          options={[
            {
              label: 'No additional trust',
              value: MaritalTrustType.None,
            },
            {
              label: 'With a disclaimer trust',
              value: MaritalTrustType.DisclaimerTrust,
            },
            {
              label: 'With a Clayton QTIP trust',
              value: MaritalTrustType.ClaytonQTIPTrust,
            },
          ]}
          row={false}
          stackingDirection="row"
        />
      </Card>
      {recipientFields.map((recipient, index) => (
        <TemplateRecipientSlot
          key={recipient._id}
          index={index}
          slotKind={recipient._templateSlotKind}
          onRemove={() => handleRemoveRecipient(index)}
        />
      ))}
    </Stack>
  );
}

const BLANK_CLAYTON_QTIP_TRUST: DispositiveProvisionsFormRecipient = {
  _templateSlotKind: DispositiveProvisionsTemplateSlotKind.MaritalClaytonQtip,
  recipientId: '', // blank string instead of null to avoid MUI warning
  recipientKind: null,
  dispositionAmount: null,
  dispositionPercentage: null,
  notes: '',
  _dispositiveProvisionId: null,
  assetGroupIds: [],
  assetStaticIds: [],
  insurancePolicyIds: [],
  ownershipStakeIds: [],
  dispositionAssetPercentage: null,
  transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseCreditShelter,
  dispositionKind_ButtonGroup: 'other',
  dispositionKind_Select:
    DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor,
};
